/**
 * Shared error types and HTTP helpers for the Agent modules.
 *
 * All known/classified errors extend `AgentApiError`, carrying a stable
 * `code` and an optional server-provided `message` (from the stream
 * protocol or a JSON error body). Unknown errors remain plain `Error`.
 */

export type AgentApiErrorCode = 'rate_limited' | 'llm_unavailable' | 'rag_unavailable' | 'auth' | 'network';

export const AGENT_ERROR_MESSAGES: Record<AgentApiErrorCode, string> = {
    rate_limited:    'Too many requests. Please wait a moment, then try again.',
    llm_unavailable: 'Our AI service is temporarily unavailable. Please try again shortly.',
    rag_unavailable: 'We could not reach the knowledge base. Please try again in a few moments.',
    auth:            'Your session has expired. Please refresh the page and try again.',
    network:         'Unable to reach the service. Check your internet connection or try again later.',
};

export class AgentApiError extends Error {
    readonly code: AgentApiErrorCode;
    readonly serverMessage?: string;

    constructor( code: AgentApiErrorCode, serverMessage?: string ) {
        super( serverMessage ?? AGENT_ERROR_MESSAGES[ code ] );
        this.name = 'AgentApiError';
        this.code = code;
        this.serverMessage = serverMessage;
    }
}

export class RateLimitError extends AgentApiError {
    constructor() {
        super( 'rate_limited' );
        this.name = 'RateLimitError';
    }
}

export class LLMUnavailableError extends AgentApiError {
    constructor( serverMessage?: string ) {
        super( 'llm_unavailable', serverMessage );
        this.name = 'LLMUnavailableError';
    }
}

export class RagUnavailableError extends AgentApiError {
    constructor( serverMessage?: string ) {
        super( 'rag_unavailable', serverMessage );
        this.name = 'RagUnavailableError';
    }
}

export class AuthError extends AgentApiError {
    constructor( serverMessage?: string ) {
        super( 'auth', serverMessage );
        this.name = 'AuthError';
    }
}

export class NetworkError extends AgentApiError {
    constructor( serverMessage?: string ) {
        super( 'network', serverMessage );
        this.name = 'NetworkError';
    }
}

/**
 * Returns a user-facing message for an error.
 * For AgentApiError, prefers the server-supplied message (stream protocol),
 * otherwise falls back to the client default for that code.
 */
export function getAgentErrorMessage( error: unknown ): string {
    if ( error instanceof AgentApiError ) {
        return error.serverMessage ?? AGENT_ERROR_MESSAGES[ error.code ];
    }
    return 'Something went wrong. Please try again.';
}

/**
 * Wraps `fetch` to translate low-level network failures into `NetworkError`.
 * User-initiated `AbortError` is re-thrown untouched so callers can keep
 * their stop-response flow. HTTP responses (including 4xx/5xx) pass through —
 * they are classified by `classifyHttpError`.
 */
export async function safeFetch( input: RequestInfo | URL, init?: RequestInit ): Promise<Response> {
    try {
        return await fetch( input, init );
    } catch ( err ) {
        if ( err instanceof DOMException && err.name === 'AbortError' ) {
            throw err;
        }
        throw new NetworkError();
    }
}

async function extractErrorDetail( response: Response ): Promise<string | undefined> {
    try {
        const payload = await response.json();
        if ( payload?.detail ) return String( payload.detail );
        if ( payload?.message ) return String( payload.message );
        return undefined;
    } catch {
        try {
            const text = await response.text();
            return text || undefined;
        } catch {
            return undefined;
        }
    }
}

/**
 * Converts a stream-protocol `3:` error payload into an Error.
 *
 * Structured payloads have the shape `{"type": "<code>", "message": "<text>"}`.
 * When a known `type` is present, an `AgentApiError` subclass carrying the
 * server-supplied `message` is returned so the UI can display it verbatim.
 */
export function parseStreamError( jsonValue: string ): Error {
    let parsed: unknown;
    try {
        parsed = JSON.parse( jsonValue );
    } catch {
        return new Error( 'Something went wrong. Please try again.' );
    }

    if ( typeof parsed === 'string' ) {
        return new Error( parsed );
    }
    if ( ! parsed || typeof parsed !== 'object' ) {
        return new Error( 'Something went wrong. Please try again.' );
    }

    const obj = parsed as { type?: string; message?: string; error?: string };
    const serverMessage = obj.message ?? obj.error;

    switch ( obj.type ) {
        case 'rate_limited':
            return new RateLimitError();
        case 'llm_unavailable':
            return new LLMUnavailableError( serverMessage );
        case 'rag_unavailable':
            return new RagUnavailableError( serverMessage );
        case 'auth':
            return new AuthError( serverMessage );
        default:
            return new Error( serverMessage ?? 'Something went wrong. Please try again.' );
    }
}

/**
 * Maps an error HTTP response to a typed error.
 * Reads the body for server detail when useful; falls back to a plain Error
 * for unexpected statuses so callers can still log technical info.
 */
export async function classifyHttpError( response: Response ): Promise<Error> {
    const detail = await extractErrorDetail( response );

    if ( response.status === 429 ) {
        return new RateLimitError();
    }
    if ( response.status === 401 || response.status === 403 ) {
        return new AuthError( detail );
    }
    if ( response.status === 502 || response.status === 503 || response.status === 504 ) {
        return new LLMUnavailableError( detail );
    }
    return new Error( `API error: ${ response.status }${ detail ? ` - ${ detail }` : '' }` );
}
